import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAYS = 14;
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const label = (s) => new Date(`${s}T12:00:00`).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });

/** Shows only slots really free (server-computed). options = comma-separated codes; value = { date, time } | null */
export const SlotPicker = ({ service, options, value, onChange, refreshKey }) => {
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [day, setDay] = useState(null);

  useEffect(() => {
    const start = new Date();
    start.setDate(start.getDate() + offset * DAYS);
    setData(null); setError(false);
    axios.get(`${API}/availability`, { params: { service, options, start: iso(start), days: DAYS } })
      .then(({ data: d }) => {
        setData(d);
        const firstOpen = d.days.find((x) => x.slots.length);
        setDay((cur) => (d.days.some((x) => x.date === cur && x.slots.length) ? cur : firstOpen?.date || null));
      })
      .catch(() => setError(true));
  }, [service, options, offset, refreshKey]);

  if (error) return <p className="text-sm text-amber-700" data-testid="slots-error">Les créneaux ne peuvent pas être chargés pour le moment. Vous pouvez envoyer une demande de devis.</p>;
  if (!data) return <p className="text-sm text-gray-500" data-testid="slots-loading">Chargement des créneaux…</p>;
  const current = data.days.find((x) => x.date === day);

  return (
    <div className="min-w-0" data-testid="slot-picker">
      <div className="flex items-center justify-between mb-2">
        <button type="button" disabled={offset === 0} onClick={() => setOffset(offset - 1)} className="p-2 rounded-lg disabled:opacity-30 hover:bg-gray-100" data-testid="slots-prev" aria-label="Période précédente"><ChevronLeft className="w-4 h-4" /></button>
        <span className="text-xs text-gray-500 flex items-center gap-1"><Clock className="w-3.5 h-3.5" />Durée estimée : {data.duration_minutes} min</span>
        <button type="button" onClick={() => setOffset(offset + 1)} className="p-2 rounded-lg hover:bg-gray-100" data-testid="slots-next" aria-label="Période suivante"><ChevronRight className="w-4 h-4" /></button>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-2" data-testid="slot-days">
        {data.days.filter((x) => x.slots.length).map((x) => (
          <button type="button" key={x.date} onClick={() => setDay(x.date)} data-testid={`slot-day-${x.date}`}
            className={`shrink-0 px-3 py-2 rounded-lg border text-sm capitalize transition-colors ${day === x.date ? 'border-[hsl(207,90%,54%)] bg-blue-50 text-[hsl(207,90%,44%)] font-semibold' : 'border-gray-200 hover:border-gray-300'}`}>
            {label(x.date)}
          </button>
        ))}
      </div>
      {!current ? <p className="text-sm text-gray-500 mt-2" data-testid="slots-none">Aucun créneau libre sur cette période. Essayez la période suivante.</p> : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-2">
          {current.slots.map((s) => {
            const active = value?.date === current.date && value?.time === s.time;
            return (
              <button type="button" key={s.time} onClick={() => onChange({ date: current.date, time: s.time })} data-testid={`slot-${current.date}-${s.time}`}
                className={`py-2 rounded-lg border text-sm font-medium transition-colors ${active ? 'bg-[hsl(207,90%,54%)] border-[hsl(207,90%,54%)] text-white' : 'border-gray-200 hover:border-[hsl(207,90%,54%)]'}`}>
                {s.time}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const formatSlot = (v) => (v ? `${label(v.date)} à ${v.time}` : '');
